import { forwardRef, useImperativeHandle, useState } from "react";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { toast } from "sonner";
import CustomLoader from "../static/CustomLoader";
import { uploadDocument } from "../utiles/DocumentService";

const PrintComponent = forwardRef(
  ({ formRefs, workId, activeTab, onFileUpload }, ref) => {
    const [loading, setLoading] = useState(false);
    const envCode = localStorage.getItem("envcode") || "Prod";
    const orgcode = localStorage.getItem("custCode") || "ptw";

    useImperativeHandle(ref, () => ({
      handlePrint: async () => {
        const content = formRefs[activeTab]?.current;
        if (!content) {
          toast.error("No content available to print.");
          return;
        }

        setLoading(true);
        try {
          const canvas = await html2canvas(content, {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            logging: false,
          });

          const imgData = canvas.toDataURL("image/png");
          const pdf = new jsPDF({
            orientation: "p",
            unit: "mm",
            format: "a4",
            compress: true,
          });

          const imgWidth = 210;
          const pageHeight = 297;
          const imgHeight = (canvas.height * imgWidth) / canvas.width;
          let heightLeft = imgHeight;
          let position = 0;

          pdf.addImage(
            imgData,
            "PNG",
            0,
            position,
            imgWidth,
            imgHeight,
            undefined,
            "FAST",
          );
          heightLeft -= pageHeight;

          while (heightLeft > 0) {
            position -= pageHeight;
            pdf.addPage();
            pdf.addImage(
              imgData,
              "PNG",
              0,
              position,
              imgWidth,
              imgHeight,
              undefined,
              "FAST",
            );
            heightLeft -= pageHeight;
          }

          const pdfBlob = pdf.output("blob");
          const blobURL = URL.createObjectURL(pdfBlob);
          window.open(blobURL, "_blank");
          pdf.save(`${activeTab}.pdf`);

          if (pdfBlob.size > 5 * 1024 * 1024) {
            toast.info("Large file detected, compressing...");
            await compressAndUploadPDF(pdfBlob, `${activeTab}.pdf`, 0.5);
          } else {
            await uploadPDF(pdfBlob, `${activeTab}.pdf`);
          }
        } catch (error) {
          toast.error("Failed to generate PDF.");
          console.error("PDF Generation Error:", error);
        } finally {
          setLoading(false);
        }
      },
    }));

    const uploadPDF = async (pdfBlob, fileName) => {
      setLoading(true);

      const uploadedDocs = [];
      const newUploadedFilePaths = [];

      try {
        const folderPath = `${orgcode}/ptw_${workId}`;
        const uploadRes = await uploadDocument({
          file: pdfBlob,
          projectCode: "Ptw",
          envCode,
          folderPath,
        });

        if (uploadRes?.cd === 1) {
          toast.error(uploadRes.description);
          setLoading(false);
          return;
        }

        uploadedDocs.push({
          doc: uploadRes,
          name: fileName,
          type: "application/pdf",
        });

        newUploadedFilePaths.push({
          id: Math.random().toString(36).substr(2, 9),
          doc: uploadRes,
          name: fileName,
          type: "application/pdf",
        });

        toast.success(`PDF ${fileName} uploaded successfully!`);
      } catch (uploadErr) {
        console.error(`Error uploading ${fileName}:`, uploadErr);
        toast.error(`Upload failed for ${fileName}`);
      }

      if (uploadedDocs.length > 0) {
        const docNames = uploadedDocs.map((d) => d.doc).join(",");
        const fileNames = uploadedDocs.map((d) => d.name).join(",");
        const fileTypes = uploadedDocs.map((d) => d.type).join(",");

        const workDocSave = {
          workid: workId,
          doc: docNames,
          name: fileNames,
          type: fileTypes,
        };

        console.log("Prepared CSV structure for backend:", workDocSave);
      }

      if (newUploadedFilePaths.length > 0) {
        onFileUpload(newUploadedFilePaths);
      }

      setLoading(false);
    };

    const compressAndUploadPDF = async (pdfBlob, fileName) => {
      const pdf = new jsPDF({
        orientation: "p",
        unit: "mm",
        format: "a4",
        compress: true,
      });

      const reader = new FileReader();
      reader.readAsDataURL(pdfBlob);
      reader.onloadend = async () => {
        const imgData = reader.result;
        pdf.addImage(imgData, "JPEG", 0, 0, 210, 297, undefined, "FAST");
        const compressedBlob = pdf.output("blob");

        if (compressedBlob.size < pdfBlob.size) {
          toast.success("File compressed successfully!");
          await uploadPDF(compressedBlob, fileName);
        } else {
          toast.warning(
            "Compression did not reduce size significantly. Uploading original.",
          );
          await uploadPDF(pdfBlob, fileName);
        }
      };
    };

    return <>{loading && <CustomLoader />}</>;
  },
);

export default PrintComponent;
